/**
 * UCC Filings Hooks (admin-portal)
 *
 * React Query hooks for UCC filing lifecycle operations, scoped to the
 * FI-tenant operator view. Mirrors the platform-admin useUccFilings.ts
 * pattern but imports from the admin-portal typed client layer.
 */

import { useIsFetching, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '@aaimplatform/institutional-types/ucc-filing-lifecycle';
import { uccFilingLifecycleClient } from '../api/client';

const FILINGS_KEY = 'uccFilings';
const FILING_KEY = 'uccFiling';

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || !result.data) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const errorBody = result.error as { message?: string } | undefined;
    throw new Error(errorBody?.message ?? `HTTP ${status}: ${statusText}`);
  }
  return result.data;
}

/**
 * One page of GET /ucc-filings. Status, filing type and jurisdiction filter on
 * the server, so a filter reaches every filing rather than the page on screen.
 *
 * `options.enabled` defaults to "the caller has a tenant": listFilings requires
 * a uuid tenantId, and an empty one is an HTTP 400 (GH #2242).
 */
export function useFilings(
  params: {
    tenantId: string;
    status?: components['schemas']['FilingStatus'];
    jurisdictionCode?: string;
    filingType?: components['schemas']['FilingType'];
    page?: number;
    limit?: number;
  },
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [FILINGS_KEY, params],
    queryFn: async () => {
      const result = await uccFilingLifecycleClient.GET('/ucc-filings', {
        params: {
          query: {
            tenantId: params.tenantId,
            status: params.status,
            jurisdictionCode: params.jurisdictionCode,
            filingType: params.filingType,
            page: params.page,
            limit: params.limit,
          },
        },
      });
      return unwrap(result);
    },
    enabled: options.enabled ?? !!params.tenantId,
  });
}

/** The largest page listFilings serves (limit maximum 100). */
const MAX_PAGE_SIZE = 100;
/** Pages read before a whole-status read stops and says it is incomplete. */
const MAX_PAGES = 50;

/**
 * Every filing in one status, read page by page. The spec has no expiration
 * filter, so the Expiring tile needs the whole active set, not one page of it.
 * `complete` is false when the read stopped at MAX_PAGES before `count`; the
 * caller then reports the figure as not counted rather than as a partial one.
 */
export function useAllFilings(
  params: { tenantId: string; status: components['schemas']['FilingStatus'] },
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [FILINGS_KEY, 'all', params],
    queryFn: async () => {
      const filings: components['schemas']['UccFiling'][] = [];
      let count = 0;
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const result = await uccFilingLifecycleClient.GET('/ucc-filings', {
          params: {
            query: {
              tenantId: params.tenantId,
              status: params.status,
              page,
              limit: MAX_PAGE_SIZE,
            },
          },
        });
        const body = unwrap(result);
        count = body.count;
        filings.push(...body.data);
        if (body.data.length < MAX_PAGE_SIZE || filings.length >= count) {
          return { filings, count, complete: true };
        }
      }
      return { filings, count, complete: filings.length >= count };
    },
    enabled: options.enabled ?? !!params.tenantId,
  });
}

/**
 * Refetch every filing read on the page at once (the table page, the tile
 * counts and the whole-status reads), and whether any of them is in flight.
 */
export function useFilingsRefresh() {
  const queryClient = useQueryClient();
  const fetching = useIsFetching({ queryKey: [FILINGS_KEY] });
  return {
    refresh: () => queryClient.invalidateQueries({ queryKey: [FILINGS_KEY] }),
    refreshing: fetching > 0,
  };
}

export function useFiling(filingId: string | undefined) {
  return useQuery({
    queryKey: [FILING_KEY, filingId],
    queryFn: async () => {
      const result = await uccFilingLifecycleClient.GET('/ucc-filings/{filingId}', {
        params: { path: { filingId: filingId! } },
      });
      return unwrap(result);
    },
    enabled: !!filingId,
  });
}

export function useTransitionFiling() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      filingId,
      body,
    }: {
      filingId: string;
      // The contract's TransitionRequest ({ toStatus, initiatedBy, metadata }).
      // This signature read { targetStatus, reason } behind an `as never` cast,
      // a body the lifecycle service would reject; no page called it yet.
      body: components['schemas']['TransitionRequest'];
    }) => {
      const result = await uccFilingLifecycleClient.POST('/ucc-filings/{filingId}/transitions', {
        params: { path: { filingId } },
        body,
      });
      return unwrap(result);
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [FILINGS_KEY] });
      void queryClient.invalidateQueries({ queryKey: [FILING_KEY, variables.filingId] });
    },
  });
}
